import React, { useEffect, useState } from 'react';
import { Copy, Printer, X } from 'lucide-react';
import { ClaimsService } from '../../../services/billing/claims.service';
import type { ClaimWorksheet } from '../../../dtos/billing/claims.dto';

interface ClaimWorksheetPanelProps {
  claimId: string;
  onClose: () => void;
}

const PORTAL_LABEL: Record<string, string> = {
  TEXMEDCONNECT: 'TexMedConnect',
  AVAILITY: 'Availity Essentials',
  MCO_PORTAL: 'the MCO provider portal',
  PAPER: 'a paper claim',
  OTHER: 'the payer portal'
};

async function copy(value: string) {
  try {
    await navigator.clipboard?.writeText(value);
  } catch {
    // clipboard blocked: the value is still visible to copy by hand
  }
}

/**
 * Keying worksheet (Phase 17 REQ-CLM-07): the claim's values in the submission portal's screen
 * order, with a copy button per value and a print layout. Loaded on open; every view is audited.
 */
const ClaimWorksheetPanel: React.FC<ClaimWorksheetPanelProps> = ({ claimId, onClose }) => {
  const [sheet, setSheet] = useState<ClaimWorksheet | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    ClaimsService.getClaimWorksheet(claimId)
      .then((s) => { if (!cancelled) setSheet(s); })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the worksheet'); });
    return () => { cancelled = true; };
  }, [claimId]);

  const portal = sheet?.portal ? PORTAL_LABEL[sheet.portal] || sheet.portal : 'the payer portal';

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 text-left print:border-0" aria-label="Claim keying worksheet" data-testid="claim-worksheet">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800">Keying worksheet</h3>
          {sheet && (
            <p className="text-sm text-slate-600">
              {sheet.claimForm === 'UB04' ? 'UB-04' : 'CMS-1500'} · enter in {portal}, top to bottom
              {sheet.submitTo === 'TMHP' ? ' · submit to TMHP' : ''}
            </p>
          )}
          {sheet?.orderSource === 'FORM_ORDER' && <p className="text-xs text-amber-700">No portal is set for this payer, so fields follow the form order.</p>}
        </div>
        <div className="flex gap-2 print:hidden">
          <button type="button" onClick={() => window.print()} aria-label="Print worksheet" className="text-slate-500 hover:text-slate-800"><Printer size={18} /></button>
          <button type="button" onClick={onClose} aria-label="Close worksheet" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
      </div>

      {error && <p role="alert" className="mt-3 text-sm text-red-700">{error}</p>}
      {!sheet && !error && <p className="mt-3 text-sm text-slate-500">Loading…</p>}

      {sheet?.sections.map((section) => (
        <div key={section.title} className="mt-4">
          <h4 className="text-xs font-semibold uppercase text-slate-500">{section.title}</h4>
          {section.fields.length > 0 && (
            <dl className="mt-1 divide-y divide-slate-100 text-sm">
              {section.fields.map((f) => (
                <div key={`${section.title}-${f.key}`} className="flex items-center justify-between gap-3 py-1">
                  <dt className="text-slate-500">{f.label}</dt>
                  <dd className="flex items-center gap-2 font-mono text-slate-900">
                    <span>{f.value || '—'}</span>
                    {f.value && (
                      <button type="button" onClick={() => copy(f.value)} aria-label={`Copy ${f.label}`} className="text-slate-400 hover:text-slate-700 print:hidden"><Copy size={14} /></button>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          {section.lines && (
            <div className="mt-1 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-1 pr-3 text-left">Line</th>
                    {sheet.claimForm === 'UB04' && <th className="pr-3 text-left">Rev</th>}
                    <th className="pr-3 text-left">Code</th>
                    <th className="pr-3 text-left">Date</th>
                    <th className="pr-3 text-right">Units</th>
                    <th className="text-right">Charges</th>
                  </tr>
                </thead>
                <tbody>
                  {section.lines.map((l) => (
                    <tr key={l.line} className="border-t border-slate-100 font-mono">
                      <td className="py-1 pr-3">{l.line}</td>
                      {sheet.claimForm === 'UB04' && <td className="pr-3">{l.revenueCode}</td>}
                      <td className="pr-3">{l.procedure}</td>
                      <td className="pr-3">{l.serviceDate}</td>
                      <td className="pr-3 text-right">{l.units}</td>
                      <td className="text-right">{Number(l.charges || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
      {sheet?.ruleSource && <p className="mt-4 text-xs text-slate-400">Claim form rule: {sheet.ruleSource}</p>}
    </section>
  );
};

export default ClaimWorksheetPanel;
